import { useEffect, useState, type FC, Fragment, type UIEvent } from 'react'
import { observer } from 'mobx-react-lite'
import { Typography, SpinnerLoader, Scrollbar } from 'shared/ui'
import { PhoneDropdownCard } from 'entities/Phone'
import { type Phone } from 'entities/Phone/model/Phone'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type SearchDropdownNumbersStore } from 'widgets/SearchDropdown/store/searchDropdownNumbersStore'
import { getLimitReachedTooltip } from 'widgets/SearchDropdown/constants/limitReachedTooltip'
import styles from './styles.module.scss'

type INumbersDropdownContentItemsProps = {
  store: SearchDropdownNumbersStore
  onChange?: (item: Phone) => void
}

export const NumbersDropdownContentItems: FC<INumbersDropdownContentItemsProps> = observer(
  ({ store, onChange }) => {
    const {
      loading,
      hasMore,
      itemsListIds,
      loadMore,
      hasItems,
      init,
      options,
      maxLimit,
      selectedItemsCount,
    } = store
    const isMaxLimitReached = maxLimit !== null && selectedItemsCount >= maxLimit
    const [selectedIds, setSelectedIds] = useState<number[]>([])
    const { numbersStore } = useEntitiesContext()

    const handleScroll = (e: UIEvent) => {
      const target = e.target as HTMLDivElement
      const scrollHeight = target.scrollHeight
      const scrollTop = target.scrollTop
      const offsetHeight = target.offsetHeight

      if (loading) return
      if (!hasMore) return
      if (scrollHeight <= scrollTop + offsetHeight + 100) {
        loadMore()
      }
    }

    const handleSelect = (item: Phone) => {
      onChange?.(item)
      store.selectTypeItem(item)
    }

    const isSelected = (id: number | undefined) => {
      if (!id) return false

      return store.isSelected(id)
    }

    useEffect(() => {
      setSelectedIds(store.selectedItemsListIds)

      if (!loading) {
        init()
      }
    }, [])

    return (
      <Scrollbar autoHeight autoHeightMin={0} autoHeightMax={290} onScroll={handleScroll}>
        <div className={styles.list}>
          {[...selectedIds, ...itemsListIds].map((id, index) => {
            const item = numbersStore.getItem(id)
            if (!item) return

            const isDisabled = isMaxLimitReached && !isSelected(id)
            const tooltipProps = isMaxLimitReached ? getLimitReachedTooltip() : undefined
            const inputCheckboxProps = options?.selectedMode
              ? {
                  checked: isSelected(id),
                  onChecked: () => handleSelect(item),
                }
              : undefined

            return (
              <Fragment key={item.id}>
                <PhoneDropdownCard
                  InputCheckboxProps={inputCheckboxProps}
                  onChange={handleSelect}
                  item={item}
                  tooltipProps={tooltipProps}
                  disabled={isDisabled}
                />
                {index === selectedIds.length - 1 && <div className={styles.divider}></div>}
              </Fragment>
            )
          })}
          {!hasItems && !loading && (
            <div className={styles.epmtyList}>
              <Typography variant={'body-rg-regular'} ariaLabel={'Not found'}>
                Not found
              </Typography>
            </div>
          )}
          {loading && (
            <div className={styles.spinner}>
              <SpinnerLoader ariaLabel={'NumbersDropdownContentItems'} />
            </div>
          )}
        </div>
      </Scrollbar>
    )
  }
)
